import React, { useMemo } from 'react';
import { 
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Chip, Stack, Card, CardContent 
} from '@mui/material';
import { CheckCircle as SuccessIcon, Error as ErrorIcon } from '@mui/icons-material';

export default function Step4Validation({ data }) {
  const staffList = Object.values(data || {});

  const validationData = useMemo(() => {
    let validCount = 0;
    let invalidCount = 0;
    
    const records = staffList.map(staff => {
      // Aggregate staff level errors and event level errors
      const errors = [...(staff.errors || [])];
      
      (staff.events || []).forEach((event, idx) => {
        if (event.errors && event.errors.length > 0) {
          event.errors.forEach(err => {
            errors.push(`Row ${idx + 1}: ${err}`);
          });
        }
      });

      const isValid = errors.length === 0;
      if (isValid) validCount++;
      else invalidCount++;

      return {
        staff_code: staff.staff_code,
        full_name: staff.full_name,
        salutation: staff.salutation,
        email: staff.email,
        nrc_number: staff.nrc_number,
        isValid,
        errors
      };
    });

    return { records, validCount, invalidCount };
  }, [staffList]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>4. Validation & Dry Run</Typography>
      <Typography variant="body2" color="textSecondary" mb={3}>
        Review the validation results. Records with errors will prevent the import from executing.
      </Typography>

      <Stack direction="row" spacing={3} sx={{ mb: 4 }}>
        <Card sx={{ flex: 1, bgcolor: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 4 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 900, color: '#047857' }}>{validationData.validCount}</Typography>
            <Typography variant="body2" sx={{ color: '#065f46', fontWeight: 700 }}>Ready to Import</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 900, color: '#b91c1c' }}>{validationData.invalidCount}</Typography>
            <Typography variant="body2" sx={{ color: '#991b1b', fontWeight: 700 }}>Staff with Errors</Typography>
          </CardContent>
        </Card>
      </Stack>

      <TableContainer component={Paper} sx={{ borderRadius: 4, maxHeight: 600, border: '1px solid #e2e8f0' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Staff Code</TableCell>
              <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Full Name</TableCell>
              <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>NRC Number</TableCell>
              <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Validation Errors</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {validationData.records.map((row, index) => (
              <TableRow key={index} sx={{ bgcolor: row.isValid ? 'inherit' : '#fff5f5' }}>
                <TableCell>
                  <Chip
                    label={row.isValid ? "Ready" : "Invalid"}
                    color={row.isValid ? "success" : "error"}
                    size="small"
                    sx={{ fontWeight: 800 }}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{row.staff_code || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  {row.salutation ? `${row.salutation} ` : ''}{row.full_name || '-'}
                </TableCell>
                <TableCell>{row.nrc_number || '-'}</TableCell>
                <TableCell sx={{ color: '#b91c1c', fontWeight: 600 }}>
                  {row.errors.length > 0 ? (
                    <Stack spacing={0.5}>
                      {row.errors.map((err, idx) => (
                        <Typography key={idx} variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ErrorIcon sx={{ fontSize: 14 }} /> {err}
                        </Typography>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="caption" sx={{ color: '#047857', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <SuccessIcon sx={{ fontSize: 14 }} /> Pass
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
